import React from "react";
import { Container, Section, PageHero, Card, Eyebrow, CTABand } from "@/components/ui-bits/Primitives";
import { TECH_FEATURES, IMAGES } from "@/lib/site-data";
import { Apple, CreditCard, Banknote } from "lucide-react";

export default function Technology() {
    return (
        <div data-testid="technology-page">
            <PageHero
                kicker="Technology Platform"
                title="One network. Every payment. Total visibility."
                lead="OCPP-compliant infrastructure, real-time diagnostics, and a payments stack that meets drivers and fleets where they are."
                image={IMAGES.blueprint}
            />

            <Section>
                <Container>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {TECH_FEATURES.map((t) => {
                            const I = t.icon;
                            return (
                                <Card key={t.title}>
                                    <I className="w-7 h-7 text-electric-400 mb-5" strokeWidth={1.5} />
                                    <h3 className="font-heading text-lg text-white">{t.title}</h3>
                                    <p className="mt-3 text-sm text-slate-400 leading-relaxed">{t.body}</p>
                                </Card>
                            );
                        })}
                    </div>

                    <div className="mt-16 border border-white/10 p-8 md:p-12 bg-ink-700/40">
                        <Eyebrow className="mb-4">Payments</Eyebrow>
                        <h3 className="font-heading text-2xl md:text-3xl text-white tracking-tight">Every way drivers and fleets want to pay.</h3>
                        <p className="mt-4 text-slate-400 leading-relaxed max-w-3xl">From tap-to-pay smartphones to traditional fleet cards — and on-site cash kiosks for unbanked drivers — our payment stack meets every community where it is.</p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            {["Apple Pay", "Google Pay", "Tap to Pay", "RFID Cards", "WEX Fleet", "WEX EFS", "Plug & Charge"].map((p) => (
                                <div key={p} className="border border-white/10 px-4 py-2 text-xs uppercase tracking-[0.18em] text-slate-300 flex items-center gap-2 bg-ink-900/60">
                                    <CreditCard className="w-3.5 h-3.5 text-electric-400" /> {p}
                                </div>
                            ))}
                            <div className="border border-electric-400/40 px-4 py-2 text-xs uppercase tracking-[0.18em] text-electric-400 flex items-center gap-2 bg-electric-400/5">
                                <Banknote className="w-3.5 h-3.5" /> Cash-to-RFID Kiosk
                            </div>
                        </div>

                        {/* Cash Kiosk callout */}
                        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 border-t border-white/10 pt-10">
                            <div className="lg:col-span-1">
                                <div className="w-12 h-12 border border-electric-400/40 bg-electric-400/5 flex items-center justify-center"><Banknote className="w-6 h-6 text-electric-400" strokeWidth={1.5} /></div>
                            </div>
                            <div className="lg:col-span-7">
                                <div className="text-[11px] uppercase tracking-[0.22em] text-electric-400 mb-2">Cash Kiosk Program</div>
                                <h4 className="font-heading text-xl md:text-2xl text-white tracking-tight">Cash-to-RFID conversion for unbanked drivers and product purchases.</h4>
                                <p className="mt-4 text-slate-400 leading-relaxed">On-site cash kiosks accept bills and instantly load value onto stored-value RFID cards — usable for charging sessions and on-site product purchases. Designed for equitable access in rural and underbanked communities.</p>
                            </div>
                            <div className="lg:col-span-4 grid grid-cols-2 gap-3 self-start">
                                {[
                                    { k: "Cash", v: "$1–$100 bills" },
                                    { k: "Instant", v: "RFID activation" },
                                    { k: "Reload", v: "Top-up anytime" },
                                    { k: "Equity", v: "Unbanked-friendly" },
                                ].map((s) => (
                                    <div key={s.k} className="border border-white/10 p-3">
                                        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{s.k}</div>
                                        <div className="text-sm text-white mt-1">{s.v}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </Container>
            </Section>

            <CTABand title="Integrate M-Class into your fleet stack." subtitle="API-first, telematics-friendly, and built for real operations." />
        </div>
    );
}
